import React from 'react';
import { DebugService, DefaultButton } from '@inmoment/the-kitchen'; // Also loads 'theKitchen' global variable

interface DebugServicePlaygroundProps {
  /**
   * The element that contains the component.
   */
  componentWrapperElement: HTMLElement | null;
  componentName: string;
}

export const DebugServicePlayground = ({
  componentWrapperElement: wrapperElement,
  componentName,
}: DebugServicePlaygroundProps): JSX.Element | null => {
  const [componentDebugService, setComponentDebugService] = React.useState<DebugService>();
  /** Used inside effects */
  const componentDebugServiceRef = React.useRef(componentDebugService);
  componentDebugServiceRef.current = componentDebugService;

  const updateComponentDebugService = React.useCallback(() => {
    if (!wrapperElement || !theKitchen) {
      return undefined;
    }

    const targets = Array.from(wrapperElement.querySelectorAll<HTMLElement>('[data-kitchen-debug-id]')).filter((el) =>
      el.getAttribute('data-kitchen-debug-id')!.startsWith(`${componentName}-`),
    );

    if (targets.length === 0) {
      // Don't warn because the length might be zero until the children fully render.
      return;
    } else if (targets.length > 1) {
      console.warn(`DebugServicePlayground: More than one ${componentName} component found within`, wrapperElement);
    }

    const target = targets[0];
    const debugId = target.getAttribute('data-kitchen-debug-id')!;
    const debugService = theKitchen.components.mounted[debugId];

    if (debugService !== componentDebugServiceRef.current) {
      setComponentDebugService(debugService);
    }
  }, [componentName, wrapperElement]);

  React.useEffect(() => {
    if (theKitchen && !theKitchen.isEnabled) {
      theKitchen.isEnabled = true;
      updateComponentDebugService();
    }
  });

  React.useEffect(() => {
    if (!wrapperElement || !theKitchen) {
      return;
    }

    const onMutation = () => updateComponentDebugService();
    onMutation();

    const observer = new MutationObserver(onMutation);
    observer.observe(wrapperElement, { childList: true, attributes: true, subtree: true });
    return () => observer.disconnect();
  }, [updateComponentDebugService, wrapperElement]);

  return componentDebugService ? (
    <div>
      <DefaultButton text="View DebugService data" onClick={() => componentDebugService.view()} stretch />
    </div>
  ) : null;
};
